import Layout from '../components/MyLayout.js';
import fetch from 'isomorphic-unfetch';
import axios from 'axios';
import React from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import imageTools from '../utils/imagetools';
import apiBase from '../utils/apiBase';
import css from '../sass/sitestyle.scss';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

const ratingFields = [
  ['Meat flavour', 'meatFlavour'],
  ['Meat texture', 'meatTexture'],
  ['Meat succulence', 'meatSucculence'],
  ['Meat ratio', 'meatVolume'],
  ['Bun', 'bunRating'],
  ['Toppings', 'toppingRating'],
  ['Sides', 'sideRating'],
];

const resizeImage = (file, size) =>
  new Promise((resolve) => {
    imageTools.resize(file, { width: size, height: size }, resolve);
  });

const getTopBurgers = async () => {
  const response = await fetch(`${apiBase}/top/10`);
  return response.json();
};

const getBurger = async (id) => {
  const response = await fetch(`${apiBase}/burger/${id}`);
  return response.json();
};

export default class Edit extends React.Component {
  constructor(props) {
    super(props);
    this.state = Object.assign({}, props.burger, {
      file: null,
      dateEaten: moment.unix(parseInt(props.burger.dateEaten, 10)),
      authChecked: false,
      error: '',
      saved: false,
      submitting: false,
    });
    this.onChange = this.onChange.bind(this);
    this.onFileChange = this.onFileChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  static async getInitialProps({ query }) {
    const results = await Promise.all([getTopBurgers(), getBurger(query.id)]);
    return { topBurgers: results[0], burger: results[1] };
  }

  async componentDidMount() {
    try {
      const response = await fetch(`${apiBase}/session`, { credentials: 'include' });
      if (!response.ok) return window.location.assign('/login');
      this.setState({ authChecked: true });
    } catch (err) {
      window.location.assign('/login');
    }
  }

  onChange(event) {
    this.setState({ [event.target.name]: event.target.value, saved: false });
  }

  onFileChange(event) {
    this.setState({ file: event.target.files[0], saved: false });
  }

  async onSubmit(event) {
    event.preventDefault();
    this.setState({ submitting: true, error: '', saved: false });
    try {
      const form = new FormData();
      [
        'restaurantName', 'burgerName', 'burgerNotes', 'lat', 'long',
        'meatFlavour', 'meatTexture', 'meatSucculence', 'meatVolume',
        'bunRating', 'toppingRating', 'sideRating',
      ].forEach((field) => form.set(field, this.state[field] || ''));
      form.set('dateEaten', this.state.dateEaten.unix());

      if (this.state.file) {
        const images = await Promise.all([
          resizeImage(this.state.file, 256),
          resizeImage(this.state.file, 1500),
        ]);
        form.set('icon', images[0]);
        form.set('image', images[1]);
      }

      await axios.post(`${apiBase}/burger/${this.props.burger.burgerID}`, form, {
        headers: { 'content-type': 'multipart/form-data' },
        withCredentials: true,
      });
      this.setState({ submitting: false, saved: true, file: null });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        return window.location.assign('/login');
      }
      const message = err.response && err.response.data && err.response.data.err
        ? err.response.data.err
        : 'The review could not be updated.';
      this.setState({ submitting: false, error: message });
    }
  }

  renderRating(label, name) {
    return (
      <fieldset className={css['rating-field']} key={name}>
        <legend>{label}</legend>
        <div className={css['rating-options']}>
          {[1,2,3,4,5,6,7,8,9,10].map((score) => (
            <label key={name + score} htmlFor={name + score}>
              <input id={name + score} type="radio" name={name} value={score}
                checked={parseInt(this.state[name], 10) === score} onChange={this.onChange} />
              {score}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  render() {
    if (!this.state.authChecked) {
      return <Layout {...this.props}><div className={css.loading}>Checking login…</div></Layout>;
    }
    return (
      <Layout {...this.props}>
        <div className={css['submit-page']}>
          <div className={css['page-heading']}>
            <p className={css.eyebrow}>Review editor</p>
            <h1>Edit {this.props.burger.burgerName}</h1>
            <p>Replace a missing photo or correct the original review.</p>
          </div>
          <form className={css['burger-form']} onSubmit={this.onSubmit}>
            {this.state.error ? <p className={css.alert} role="alert">{this.state.error}</p> : null}
            {this.state.saved ? <p className={css.success} role="status">Review saved. <a href={`/b/${this.props.burger.burgerID}`}>View it</a></p> : null}
            <section className={css['form-section']}>
              <h2>Photo</h2>
              <div className={css['current-image']}>
                <img src={`/image/${this.props.burger['img:filename']}`} onError={(e) => { e.target.src='/image/fallback/burger-fallback.svg'; }} alt="Current burger" />
              </div>
              <label htmlFor="replacementPhoto">Replacement photo (optional)</label>
              <input id="replacementPhoto" type="file" accept="image/*" onChange={this.onFileChange} />
            </section>
            <section className={css['form-section']}>
              <h2>Details</h2>
              <div className={css['form-grid']}>
                <div><label htmlFor="restaurantName">Restaurant</label><input id="restaurantName" name="restaurantName" required value={this.state.restaurantName || ''} onChange={this.onChange} /></div>
                <div><label htmlFor="burgerName">Burger name</label><input id="burgerName" name="burgerName" required value={this.state.burgerName || ''} onChange={this.onChange} /></div>
                <div><label htmlFor="dateEaten">Date eaten</label><DatePicker name="dateEaten" selected={this.state.dateEaten} onChange={(date) => this.setState({ dateEaten: date })} /></div>
              </div>
              <label htmlFor="burgerNotes">Tasting notes</label>
              <textarea id="burgerNotes" name="burgerNotes" rows="6" value={this.state.burgerNotes || ''} onChange={this.onChange} />
            </section>
            <section className={css['form-section']}>
              <h2>Scores</h2>
              <div className={css['ratings-grid']}>{ratingFields.map((field) => this.renderRating(field[0], field[1]))}</div>
            </section>
            <button className={css['primary-button']} disabled={this.state.submitting} type="submit">
              {this.state.submitting ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </div>
      </Layout>
    );
  }
}



// WEBPACK FOOTER //
// pages/edit.js